<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { MAX_FILES_PER_BATCH } from 'dashboard/composables/useArrowsAiResources';

const emit = defineEmits(['add']);
const { t } = useI18n();

const IMAGE_MAX = 5 * 1024 * 1024;
const MEDIA_MAX = 16 * 1024 * 1024;
const ACCEPTED =
  'image/jpeg,image/png,video/mp4,audio/mpeg,application/pdf,.jpg,.jpeg,.png,.mp4,.mp3,.pdf';

const isDragging = ref(false);
const inputRef = ref(null);

const maxFor = name => (/\.(jpe?g|png)$/i.test(name) ? IMAGE_MAX : MEDIA_MAX);

const isAccepted = file => /\.(jpe?g|png|mp4|mp3|pdf)$/i.test(file.name);

const collectFiles = fileList => {
  const files = Array.from(fileList || []);
  if (files.length > MAX_FILES_PER_BATCH) {
    useAlert(t('ARROWS_AI.RESOURCES.TOO_MANY', { max: MAX_FILES_PER_BATCH }));
    return;
  }

  const valid = [];
  files.forEach(file => {
    if (!isAccepted(file)) {
      useAlert(t('ARROWS_AI.RESOURCES.INVALID'));
      return;
    }
    if (file.size > maxFor(file.name)) {
      useAlert(t('ARROWS_AI.RESOURCES.TOO_LARGE'));
      return;
    }
    valid.push(file);
  });

  if (valid.length) emit('add', valid);
};

const onDrop = event => {
  isDragging.value = false;
  collectFiles(event.dataTransfer?.files);
};
</script>

<template>
  <button
    type="button"
    class="flex flex-col items-center justify-center w-full gap-2 px-6 py-12 text-center custom-dashed-border rounded-2xl bg-n-solid-1"
    :class="{ 'bg-n-alpha-2': isDragging }"
    @click="inputRef.click()"
    @dragenter.prevent="isDragging = true"
    @dragover.prevent="isDragging = true"
    @dragleave.prevent="isDragging = false"
    @drop.prevent="onDrop"
  >
    <span class="i-lucide-upload size-6 text-n-slate-11" />
    <span class="text-sm font-medium text-n-slate-12">
      {{ t('ARROWS_AI.RESOURCES.DROP_TITLE') }}
    </span>
    <span class="text-sm text-n-slate-11">
      {{ t('ARROWS_AI.RESOURCES.DROP_SUBTITLE') }}
    </span>
    <input
      ref="inputRef"
      type="file"
      multiple
      class="hidden"
      :accept="ACCEPTED"
      @change="collectFiles($event.target.files)"
    />
  </button>
</template>
